import { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowRight, CircleCheck, Lock, Award, Truck, MapPin, Clock, Home as HomeIcon } from "lucide-react";

const PRODUCT_SLUGS = [
  "rouleau-adhesif",
  "devidoir-pistolet-adhesif",
  "marqueur-permanent",
  "cutter",
  "chariot-demenagement",
  "diable",
];

const FermetureAccessoires = () => {
  useDocumentTitle(
    "Fermeture et accessoires déménagement | cartonmove",
    "Rouleau d'adhésif, dévidoir pistolet, marqueur permanent, cutter, chariot et diable de déménagement. Tous les accessoires pour préparer votre déménagement à Paris et en Île-de-France."
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const items = PRODUCT_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div data-testid="fermeture-accessoires-page">
      <CategoriesSubnav />

      {/* TOP — H1 + produits + intro */}
      <section className="section-pad bg-white" id="produits">
        <div className="container-cdm">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline justify-center">Boutique cartonmove</div>
            <h1 className="mt-3 text-[28px] sm:text-[33px] md:text-[48px] font-extrabold text-[#0f1d2b] leading-tight">
              Fermeture et accessoires déménagement
            </h1>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="fermeture-grid">
              {items.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty ctaLabel="Acheter" />
              ))}
            </div>
          )}

          {/* Intro */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Découvrez notre catalogue, gamme <strong>dispositifs de scellage</strong>, accessoires
              de fermeture pour sécuriser, sceller et stabiliser vos volumes&nbsp;: fournitures
              professionnelles et haute résistance pour garantir un maintien parfait de vos mobiliers
              et équipements. Accessoires techniques indispensables pour verrouiller la protection de
              vos biens lors d'un déménagement à <strong>Paris</strong> et dans toute{" "}
              <strong>l'Île-de-France</strong>, qu'il s'agisse de transferts résidentiels,
              garde-meubles ou logistique d'entreprise.
            </p>
          </div>

          {/* CTA Big Déménagement */}
          <div className="mt-12">
            <BigDemenageurCTA testId="fermeture-bigdem-cta" />
          </div>

          {/* LIVRAISON IDF */}
          <div className="mt-12 max-w-4xl mx-auto bg-white border border-[#e6edf3] rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="overline">Service de livraison</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Distribution rapide d'accessoires de fermeture en Île-de-France
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Notre équipe prend en charge le transport de vos accessoires de fermeture de sécurité,
              uniquement en région parisienne.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Afin de respecter vos disponibilités, nous convenons ensemble d'un créneau horaire
              précis. Vos fournitures de fixation vous sont remises à la date fixée, vous garantissant
              une organisation fluide et sans contretemps pour vos cartons.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">Nos engagements pour votre livraison&nbsp;:</h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="fermeture-livraison-highlights">
              {[
                { icon: Truck, title: "Équipe dédiée", desc: "Acheminement réalisé par nos propres transporteurs." },
                { icon: MapPin, title: "Proximité régionale", desc: "Livraison réservée exclusivement aux départements d'Île-de-France." },
                { icon: Clock, title: "Flexibilité horaire", desc: "Organisation du rendez-vous selon votre emploi du temps." },
                { icon: HomeIcon, title: "Service à l'adresse", desc: "Matériel déposé directement à l'intérieur de votre domicile ou de vos locaux." },
              ].map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="flex items-start gap-3 bg-[#f7fafc] rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[14.5px] font-extrabold text-[#0f1d2b]">{title}</div>
                    <div className="text-[13.5px] text-[#3a4a5a] leading-[1.55] mt-0.5">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEO sections */}
      <section className="section-pad bg-[#EEF3F7] border-t border-[#e6edf3]">
        <div className="container-cdm max-w-4xl space-y-10">
          {/* Guide d'estimation */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Guide d'estimation&nbsp;: combien de rouleaux pour votre déménagement&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Pour éviter de vous retrouver à court de fournitures au milieu de vos cartons, voici
              notre estimation des besoins moyens en rouleaux (adhésifs de fermeture et films de
              protection) calculée par nos experts de terrain.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">
              Estimations par type de logement
            </h3>
            <div className="mt-3 overflow-hidden rounded-2xl border border-[#e6edf3] bg-white">
              <table className="w-full text-[14.5px]" data-testid="fermeture-rouleaux-table">
                <thead>
                  <tr className="bg-[#237cb5] text-white">
                    <th className="text-left px-4 sm:px-5 py-3 font-bold">Taille du logement</th>
                    <th className="text-left px-4 sm:px-5 py-3 font-bold">Rouleaux d'adhésif</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Studio / F1", "2 à 3 rouleaux"],
                    ["Appartement 2-3 pièces (F2 / F3)", "4 à 6 rouleaux"],
                    ["Maison / Grand appartement (F4+)", "8 rouleaux et plus"],
                  ].map(([logement, rouleaux], i) => (
                    <tr key={logement} className={i % 2 === 0 ? "bg-white" : "bg-[#f7fafc]"}>
                      <td className="px-4 sm:px-5 py-3 font-semibold text-[#0f1d2b]">{logement}</td>
                      <td className="px-4 sm:px-5 py-3 text-[#3a4a5a]">{rouleaux}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                <strong>Petit repère pour calculer correctement&nbsp;:</strong> 1 rouleau d'adhésif
                standard (50 mètres) permet de fermer de manière sécurisée (haut et bas) environ{" "}
                <strong>15 à 20 cartons</strong> de taille moyenne.
              </p>
              <div className="rounded-2xl bg-[#FFF6EE] border border-[#FFE0C2] p-5">
                <div className="text-[12px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                  Notre conseil de pro
                </div>
                <p className="mt-2 text-[#3a4a5a]">
                  Prévoyez toujours <strong>1 ou 2 rouleaux d'adhésif supplémentaires</strong>. C'est
                  l'accessoire que l'on utilise toujours plus que prévu pour consolider les objets
                  lourds ou sécuriser les angles&nbsp;!
                </p>
              </div>
            </div>
          </article>

          {/* Pourquoi nos accessoires */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Pourquoi choisir nos accessoires de fermeture&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Parce que la sécurité de vos biens commence dès l'emballage, nous proposons des
              accessoires de <strong>fermeture fiables</strong> et adaptés aux exigences du
              déménagement. Rubans adhésifs, solutions de maintien et matériel de conditionnement
              professionnel vous permettent de protéger efficacement vos cartons et vos effets
              personnels tout au long du transport.
            </p>
          </article>

          {/* FAQ */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              FAQ&nbsp;: Fermeture et accessoires
            </h2>
            <div className="mt-5 space-y-4" data-testid="fermeture-faq">
              {[
                {
                  q: "Ces accessoires conviennent-ils aux déménagements longue distance ?",
                  r: "Oui, nos produits sont sélectionnés pour assurer une fermeture fiable et une bonne protection des cartons pendant le transport.",
                },
                {
                  q: "Puis-je acheter de petites quantités ?",
                  r: "Oui, nos accessoires sont disponibles aussi bien pour des besoins ponctuels que pour des volumes plus importants.",
                },
                {
                  q: "Vos accessoires permettent-ils de protéger les objets fragiles ?",
                  r: "Ils contribuent à sécuriser les emballages, mais pour les objets fragiles, nous recommandons également l'utilisation de protections adaptées comme le papier bulle ou les housses de protection.",
                },
              ].map(({ q, r }) => (
                <details
                  key={q}
                  className="group rounded-2xl bg-white border border-[#e6edf3] p-5 sm:p-6 open:shadow-[0_10px_25px_-12px_rgba(35,124,181,0.25)]"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 text-[15px] sm:text-base font-extrabold text-[#0f1d2b]">
                    <span>{q}</span>
                    <span className="shrink-0 mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#eaf3fb] text-[#237cb5] text-lg font-bold transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[14.5px] sm:text-[15px] text-[#3a4a5a] leading-[1.8]">{r}</p>
                </details>
              ))}
            </div>
          </article>

          {/* Garanties */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Vos garanties de confiance
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Commandez vos équipements l'esprit tranquille grâce à notre système de{" "}
              <strong>paiement 100 % sécurisé</strong>. Nous vous garantissons un matériel de qualité
              professionnelle et une totale transparence sur le suivi de votre commande.
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Lock, text: "Paiement 100 % sécurisé" },
                { icon: Award, text: "Qualité professionnelle" },
                { icon: CircleCheck, text: "Transparence sur le suivi" },
              ].map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 bg-[#f7fafc] rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="text-[14.5px] text-[#0f1d2b] leading-[1.6] font-semibold mt-1">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </div>
  );
};

export default FermetureAccessoires;
